import React, { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useLanguage } from '../../contexts/LanguageContext';
import api from '../../services/api';
import toast from 'react-hot-toast';
import { Gamepad2, Loader2, ChevronLeft, ChevronRight } from 'lucide-react';
import DailyExercisesHero from '../../components/student/dailyExercises/DailyExercisesHero';
import { gameCoverFromName } from '../../utils/gameCover';
import { isHiddenStudentGame } from '../../utils/hiddenGames';

const PAGE_SIZE = 20;

function LocalEsorobanNinjaCard() {
  const { t } = useTranslation();
  const cover = gameCoverFromName(t('studentGames.esorobanNinjaName'));
  return (
    <Link
      to="/student/games/esoroban-ninja"
      className="group block overflow-hidden rounded-[1.35rem] border-[3px] border-cyan-500/45 bg-white shadow-[0_8px_0_0_rgba(6,182,212,0.2)] transition hover:-translate-y-0.5 hover:border-cyan-400 hover:shadow-[0_8px_0_0_rgba(34,211,238,0.28)] focus:outline-none focus-visible:ring-2 focus-visible:ring-cyan-500 focus-visible:ring-offset-2"
    >
      <div className="relative aspect-[4/3] w-full overflow-hidden" style={cover.style} aria-hidden>
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_30%_20%,rgba(255,255,255,0.35),transparent_55%)]" />
        <div className="absolute start-2 top-2 rounded-md bg-black/35 px-1.5 py-0.5 text-[9px] font-black uppercase tracking-wide text-white backdrop-blur-sm">
          {t('studentGames.esorobanNinjaBadge')}
        </div>
        <div className="absolute inset-0 flex items-center justify-center">
          <span className="select-none text-4xl font-black tracking-tight text-white/95 drop-shadow-md sm:text-5xl">
            {cover.label}
          </span>
        </div>
        <div className="absolute bottom-2 end-2 rounded-lg bg-black/25 px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider text-white/95 backdrop-blur-sm">
          {t('studentGames.play')}
        </div>
      </div>
      <div className="border-t border-slate-900/5 bg-gradient-to-b from-white to-cyan-50/90 px-3 py-3">
        <p className="line-clamp-2 text-center text-sm font-bold leading-snug text-slate-900 group-hover:text-cyan-800">
          {t('studentGames.esorobanNinjaName')}
        </p>
      </div>
    </Link>
  );
}

const StudentGames = () => {
  const { t } = useTranslation();
  const { isRTL } = useLanguage();

  const [games, setGames] = useState([]);
  const [pagination, setPagination] = useState({ page: 1, limit: PAGE_SIZE, total: 0, pages: 1 });
  const [loading, setLoading] = useState(true);

  const loadPage = useCallback(
    async (page = 1) => {
      setLoading(true);
      try {
        const res = await api.get(`/games?page=${page}&limit=${PAGE_SIZE}&status=active`);
        if (res.data?.success && res.data.data) {
          const { games: list, pagination: pag } = res.data.data;
          const raw = Array.isArray(list) ? list : [];
          const visible = raw.filter(
            (g) => g && !g.is_deleted && !isHiddenStudentGame(g.name)
          );
          setGames(visible);
          setPagination({
            page: pag?.page ?? page,
            limit: pag?.limit ?? PAGE_SIZE,
            total: pag?.total ?? visible.length,
            pages: pag?.pages ?? 1,
          });
        }
      } catch (e) {
        console.error(e);
        toast.error(t('studentGames.listError'));
        setGames([]);
      } finally {
        setLoading(false);
      }
    },
    [t]
  );

  useEffect(() => {
    loadPage(1);
  }, [loadPage]);

  const page = pagination.page;
  const totalPages = Math.max(1, pagination.pages);

  return (
    <div className="relative mx-auto w-full max-w-6xl space-y-5 px-3 pb-8 sm:space-y-6 sm:px-4 sm:pb-10" dir={isRTL ? 'rtl' : 'ltr'}>
      <DailyExercisesHero
        title={t('studentGames.title')}
        description={t('studentGames.subtitle')}
        icon={Gamepad2}
        iconClassName="text-violet-600"
      />

      {loading ? (
        <div className="rounded-[1.35rem] border-[3px] border-violet-200/80 bg-gradient-to-br from-white to-violet-50/80 px-6 py-14 text-center shadow-[0_8px_0_0_rgba(139,92,246,0.1)]">
          <Loader2 className="mx-auto h-12 w-12 animate-spin text-violet-600" aria-hidden strokeWidth={2.25} />
          <p className="mt-4 text-sm font-bold text-slate-600 sm:text-base">{t('studentGames.loading')}</p>
        </div>
      ) : (
        <>
          <ul className="grid grid-cols-2 gap-4 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 lg:gap-5">
            <li key="local-esoroban-ninja">
              <LocalEsorobanNinjaCard />
            </li>
            {games.map((game) => {
              const cover = gameCoverFromName(game.name);
              return (
                <li key={game.id}>
                  <Link
                    to={`/student/games/${game.id}`}
                    state={{ name: game.name }}
                    className="group block overflow-hidden rounded-[1.35rem] border-[3px] border-slate-900/10 bg-white shadow-[0_8px_0_0_rgba(15,23,42,0.06)] transition hover:-translate-y-0.5 hover:border-violet-400/50 hover:shadow-[0_8px_0_0_rgba(139,92,246,0.12)] focus:outline-none focus-visible:ring-2 focus-visible:ring-violet-500 focus-visible:ring-offset-2"
                  >
                    <div
                      className="relative aspect-[4/3] w-full overflow-hidden"
                      style={cover.style}
                      aria-hidden
                    >
                      <div className="absolute inset-0 bg-[radial-gradient(circle_at_30%_20%,rgba(255,255,255,0.35),transparent_55%)]" />
                      <div className="absolute inset-0 flex items-center justify-center">
                        <span className="select-none text-4xl font-black tracking-tight text-white/95 drop-shadow-md sm:text-5xl">
                          {cover.label}
                        </span>
                      </div>
                      <div className="absolute bottom-2 end-2 rounded-lg bg-black/25 px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider text-white/95 backdrop-blur-sm">
                        {t('studentGames.play')}
                      </div>
                    </div>
                    <div className="border-t border-slate-900/5 bg-gradient-to-b from-white to-slate-50/90 px-3 py-3">
                      <p className="line-clamp-2 text-center text-sm font-bold leading-snug text-slate-900 group-hover:text-violet-800">
                        {game.name}
                      </p>
                    </div>
                  </Link>
                </li>
              );
            })}
          </ul>

          {games.length === 0 && (
            <p className="mt-4 text-center text-sm font-semibold text-slate-600">{t('studentGames.emptyMoreFromServer')}</p>
          )}

          {totalPages > 1 && (
            <nav
              className="flex flex-col gap-3 rounded-[1.35rem] border-[3px] border-slate-200/90 bg-slate-50/50 px-4 py-4 sm:flex-row sm:flex-wrap sm:items-center sm:justify-center sm:gap-4 sm:px-6"
              aria-label={t('studentGames.paginationAria')}
            >
              <button
                type="button"
                disabled={page <= 1}
                onClick={() => loadPage(page - 1)}
                className="inline-flex min-h-[2.5rem] items-center justify-center gap-1 rounded-xl border-2 border-slate-200 bg-white px-4 text-sm font-bold text-slate-800 shadow-sm transition hover:bg-sky-50 disabled:cursor-not-allowed disabled:opacity-40"
              >
                {isRTL ? <ChevronRight className="h-4 w-4 shrink-0" /> : <ChevronLeft className="h-4 w-4 shrink-0" />}
                {t('studentGames.prev')}
              </button>
              <span className="text-center text-sm font-bold tabular-nums text-slate-600 sm:min-w-[10rem]">
                {t('studentGames.pageOf', { page, pages: totalPages })}
              </span>
              <button
                type="button"
                disabled={page >= totalPages}
                onClick={() => loadPage(page + 1)}
                className="inline-flex min-h-[2.5rem] items-center justify-center gap-1 rounded-xl border-2 border-slate-200 bg-white px-4 text-sm font-bold text-slate-800 shadow-sm transition hover:bg-sky-50 disabled:cursor-not-allowed disabled:opacity-40"
              >
                {t('studentGames.next')}
                {isRTL ? <ChevronLeft className="h-4 w-4 shrink-0" /> : <ChevronRight className="h-4 w-4 shrink-0" />}
              </button>
            </nav>
          )}
        </>
      )}
    </div>
  );
};

export default StudentGames;
